<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import type { TCaptureOrigin } from '@/composition/menu-import/use-menu-capture';
  import type { ITrayTile } from '@/domain/menu-import/capture-tray';
  import { Globals } from '@/modules/Globals';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Su carta · 3 páginas» (plan §4.3): one 96×128 thumbnail per page with
   * its state line (preparing, uploading, ready, a photo warning, only the
   * beginning of a very long page, failed),
   * the warning and failure actions, «Quitar» behind ⋯ on every page, and
   * «Agregar otra página» last. A very tall page read in parts is one tile
   * («12 partes»): its actions act on every part. Warnings never block. The
   * `footer` slot holds the note for the read.
   */
  const FILE_ACCEPT =
    'image/*,.heic,.heif,application/pdf,.pdf,.xlsx,.xls,.ods,.csv,text/csv';

  const props = defineProps<{
    tiles: ITrayTile[];
    phone: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'retake', tile: ITrayTile, file: File): void;
    (e: 'use-anyway', tile: ITrayTile): void;
    (e: 'remove', tile: ITrayTile): void;
    (e: 'retry', tile: ITrayTile): void;
    (e: 'add', files: File[], origin: TCaptureOrigin): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const titleId = nextDomId('menu-tray-title');
  /** «Su carta · 3 páginas»: one tile per page the owner added. */
  const pageCount = computed(() => props.tiles.length);
  const uid = nextDomId('menu-tray');

  /** «Página 2 de 3»: describes every action of a tile, so «Quitar» says which page it removes. */
  function pageLabelId(tile: ITrayTile): string {
    return `${uid}-page-${tile.key}`;
  }

  const retakeInput = ref<HTMLInputElement | null>(null);
  const addInput = ref<HTMLInputElement | null>(null);
  /** The page «Sacar de nuevo» is replacing. */
  let retakeTile: ITrayTile | null = null;
  /** Thumbnails whose image did not load: they show the page icon instead. */
  const brokenKeys = ref<string[]>([]);
  /** «Usar igual» on pages uploaded before a reload or from another device. */
  const dismissedKeys = ref<string[]>([]);

  function imageUrl(tile: ITrayTile): string | null {
    if (tile.kind !== 'photo' && tile.kind !== 'pdf') return null;
    if (brokenKeys.value.includes(tile.key)) return null;
    if (tile.previewUrl) return tile.previewUrl;
    return tile.storageKey ? `${Globals.uploadsPath}/${tile.storageKey}` : null;
  }

  function onImageError(tile: ITrayTile) {
    if (!brokenKeys.value.includes(tile.key))
      brokenKeys.value = [...brokenKeys.value, tile.key];
  }

  function kindIcon(tile: ITrayTile): string {
    switch (tile.kind) {
      case 'pdf':
        return 'file-pdf-box-outline';
      case 'sheet':
        return 'file-table-outline';
      case 'text':
        return 'text-box-outline';
      default:
        return 'image-outline';
    }
  }

  function kindLabel(tile: ITrayTile): string | null {
    if (tile.kind === 'pdf' && tile.originPage !== null)
      return tr('menuImport.tray.pdfPage', { page: tile.originPage });
    if (tile.kind === 'sheet') return tr('menuImport.tray.sheet');
    if (tile.kind === 'text') return tr('menuImport.tray.text');
    return null;
  }

  /** «12 partes» of a very tall page read in parts, on its own line of the tag. */
  function partsLabel(tile: ITrayTile): string | null {
    return tile.parts === null
      ? null
      : String(
          proxy.$tc('menuImport.tray.parts', tile.parts, { count: tile.parts })
        );
  }

  function shownWarning(tile: ITrayTile): ITrayTile['warning'] {
    return tile.state === 'ready' && !dismissedKeys.value.includes(tile.key)
      ? tile.warning
      : null;
  }

  /** A ready page to look at again: a photo warning, or a very long page cut short. */
  function isFlagged(tile: ITrayTile): boolean {
    return shownWarning(tile) !== null || tile.cut;
  }

  function stateTone(tile: ITrayTile): string {
    if (tile.state === 'failed') return 'is-failed';
    if (tile.state !== 'ready') return 'is-pending';
    return isFlagged(tile) ? 'is-warning' : 'is-ready';
  }

  function stateIcon(tile: ITrayTile): string | null {
    if (tile.state === 'failed') return 'close-circle-outline';
    if (tile.state !== 'ready') return null;
    return isFlagged(tile) ? 'alert-outline' : 'check';
  }

  function stateText(tile: ITrayTile): string {
    const warning = shownWarning(tile);
    if (tile.state !== 'ready') return tr(`menuImport.tray.${tile.state}`);
    if (warning) return tr(`menuImport.tray.${warning}`);
    return tile.cut
      ? tr('menuImport.tray.cutShort')
      : tr('menuImport.tray.ready');
  }

  function isPending(tile: ITrayTile): boolean {
    return tile.state === 'processing' || tile.state === 'uploading';
  }

  function openRetake(tile: ITrayTile) {
    retakeTile = tile;
    retakeInput.value?.click();
  }

  function onRetakePicked(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const file = input.files?.[0] ?? null;
    input.value = '';
    const tile = retakeTile;
    retakeTile = null;
    if (file && tile) emit('retake', tile, file);
  }

  function useAnyway(tile: ITrayTile) {
    if (!dismissedKeys.value.includes(tile.key))
      dismissedKeys.value = [...dismissedKeys.value, tile.key];
    emit('use-anyway', tile);
  }

  function openAdd() {
    addInput.value?.click();
  }

  function onAddPicked(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length > 0)
      emit('add', files, props.phone ? 'camera' : 'gallery');
  }
</script>

<template>
  <section class="v2-menu-capture-tray" :aria-labelledby="titleId">
    <header class="tray-head">
      <h2 :id="titleId" class="tray-title">
        {{ $tc('menuImport.tray.title', pageCount, { count: pageCount }) }}
      </h2>
      <span class="tray-autosaved">{{ $t('menuImport.tray.autosaved') }}</span>
    </header>
    <PosUiScrollable
      class="tray-scroller"
      orientation="horizontal"
      hide-scrollbar="touch"
      arrows
    >
      <ul class="tray-list">
        <li
          v-for="(tile, index) in tiles"
          :key="tile.key"
          class="tray-tile"
          :class="stateTone(tile)"
        >
          <span :id="pageLabelId(tile)" class="tile-page-label">{{
            $t('menuImport.viewer.page', {
              page: index + 1,
              total: tiles.length,
            })
          }}</span>
          <div class="tile-frame">
            <img
              v-if="imageUrl(tile)"
              class="tile-image"
              :src="imageUrl(tile)"
              alt=""
              loading="lazy"
              decoding="async"
              @error="onImageError(tile)"
            />
            <span v-else class="tile-placeholder" aria-hidden="true">
              <PosUiIcon :name="kindIcon(tile)" :size="28" />
            </span>
            <span v-if="kindLabel(tile) || partsLabel(tile)" class="tile-tag">
              <span v-if="kindLabel(tile)" class="tag-line">{{
                kindLabel(tile)
              }}</span>
              <span v-if="partsLabel(tile)" class="tag-line">{{
                partsLabel(tile)
              }}</span>
            </span>
            <span
              v-if="isPending(tile)"
              class="tile-progress"
              aria-hidden="true"
            >
              <span
                class="progress-fill"
                :style="{ width: `${Math.round(tile.progress * 100)}%` }"
              />
            </span>
            <div class="tile-menu">
              <PosUiPopover
                placement="bottom"
                align="end"
                panel-class="tile-menu-panel"
              >
                <template #trigger="{ open, toggle }">
                  <button
                    type="button"
                    class="menu-trigger"
                    :aria-label="$t('menuImport.more')"
                    :aria-describedby="pageLabelId(tile)"
                    :aria-expanded="open ? 'true' : 'false'"
                    aria-haspopup="true"
                    @click="toggle"
                  >
                    <span class="menu-dot">
                      <PosUiIcon name="dots-horizontal" />
                    </span>
                  </button>
                </template>
                <template #default="{ close }">
                  <PosUiButton
                    color="neutral"
                    variant="ghost"
                    icon="delete-outline"
                    block
                    :aria-describedby="pageLabelId(tile)"
                    @click="
                      close();
                      emit('remove', tile);
                    "
                    >{{ $t('menuImport.tray.remove') }}</PosUiButton
                  >
                </template>
              </PosUiPopover>
            </div>
          </div>
          <p class="tile-state">
            <span
              v-if="isPending(tile)"
              class="state-spinner"
              aria-hidden="true"
            />
            <PosUiIcon
              v-else-if="stateIcon(tile)"
              :name="stateIcon(tile)"
              class="state-icon"
            />
            <span>{{ stateText(tile) }}</span>
          </p>
          <p
            v-if="tile.state === 'failed' && tile.errorKey"
            class="tile-reason"
          >
            {{ $t(tile.errorKey) }}
          </p>
          <div v-if="shownWarning(tile)" class="tile-actions">
            <button
              type="button"
              class="tile-link"
              :aria-describedby="pageLabelId(tile)"
              @click="openRetake(tile)"
            >
              {{ $t('menuImport.tray.retake') }}
            </button>
            <button
              type="button"
              class="tile-link"
              :aria-describedby="pageLabelId(tile)"
              @click="useAnyway(tile)"
            >
              {{ $t('menuImport.tray.useAnyway') }}
            </button>
            <button
              type="button"
              class="tile-link"
              :aria-describedby="pageLabelId(tile)"
              @click="emit('remove', tile)"
            >
              {{ $t('menuImport.tray.remove') }}
            </button>
          </div>
          <div v-else-if="tile.state === 'failed'" class="tile-actions">
            <button
              type="button"
              class="tile-link"
              :aria-describedby="pageLabelId(tile)"
              @click="emit('retry', tile)"
            >
              {{ $t('menuImport.tray.retry') }}
            </button>
            <button
              type="button"
              class="tile-link"
              :aria-describedby="pageLabelId(tile)"
              @click="emit('remove', tile)"
            >
              {{ $t('menuImport.tray.remove') }}
            </button>
          </div>
        </li>
        <li class="tray-tile is-add">
          <button type="button" class="add-tile" @click="openAdd">
            <PosUiIcon name="plus" :size="22" />
            <span>{{ $t('menuImport.tray.addPage') }}</span>
          </button>
        </li>
      </ul>
    </PosUiScrollable>
    <slot name="footer" />
    <input
      ref="retakeInput"
      class="file-input"
      type="file"
      accept="image/*"
      :capture="phone ? 'environment' : undefined"
      tabindex="-1"
      aria-hidden="true"
      @change="onRetakePicked"
    />
    <input
      ref="addInput"
      class="file-input"
      type="file"
      :multiple="!phone"
      :accept="phone ? 'image/*' : FILE_ACCEPT"
      :capture="phone ? 'environment' : undefined"
      tabindex="-1"
      aria-hidden="true"
      @change="onAddPicked"
    />
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-capture-tray {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;
    .tray-head {
      @apply tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-x-3 tw-gap-y-1;
    }
    .tray-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .tray-autosaved {
      @apply tw-text-xs tw-text-neutral-500;
    }
    .tray-list {
      @apply tw-m-0 tw-flex tw-list-none tw-items-start tw-gap-3 tw-p-0 tw-pb-1;
    }
    .tray-tile {
      @apply tw-flex tw-w-24 tw-shrink-0 tw-flex-col tw-gap-1;
    }
    // Read by screen readers only, as the description of the tile's actions.
    .tile-page-label {
      @apply tw-sr-only;
    }
    .tile-frame {
      @apply tw-relative tw-h-32 tw-w-24 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-100;
    }
    .tray-tile.is-warning .tile-frame {
      @apply tw-border-amber-300;
    }
    .tray-tile.is-failed .tile-frame {
      @apply tw-border-red-300;
    }
    .tile-image {
      @apply tw-block tw-h-full tw-w-full tw-object-cover;
    }
    .tile-placeholder {
      @apply tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-neutral-400;
    }
    .tile-tag {
      @apply tw-absolute tw-bottom-1.5 tw-left-1.5 tw-right-1.5 tw-flex tw-flex-col tw-rounded tw-bg-white/90 tw-px-1.5 tw-py-0.5 tw-text-center tw-text-[11px] tw-font-medium tw-text-neutral-700;
      .tag-line {
        @apply tw-truncate;
      }
    }
    .tile-progress {
      @apply tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-1 tw-bg-neutral-200;
      .progress-fill {
        @apply tw-block tw-h-full tw-bg-neutral-900 tw-transition-all tw-duration-200;
      }
    }
    // A 44 px hit area around a small quiet dot in the corner.
    .tile-menu {
      @apply tw-absolute tw-right-0 tw-top-0;
    }
    .menu-trigger {
      @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-start tw-justify-end tw-border-0 tw-bg-transparent tw-p-1;
      &:focus-visible {
        @apply tw-outline-none;
        .menu-dot {
          @apply tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }
    .menu-dot {
      @apply tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/90 tw-text-base tw-text-neutral-700 tw-shadow-sm;
    }
    :deep(.pos-ui-popover-panel.tile-menu-panel) {
      @apply tw-w-auto tw-min-w-[10rem] tw-p-1;
    }
    .tile-state {
      @apply tw-m-0 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-neutral-600;
      .state-icon {
        @apply tw-shrink-0 tw-text-sm;
      }
    }
    .tray-tile.is-ready .tile-state {
      @apply tw-text-emerald-700;
    }
    .tray-tile.is-warning .tile-state {
      @apply tw-text-amber-800;
    }
    .tray-tile.is-failed .tile-state {
      @apply tw-text-red-700;
    }
    .state-spinner {
      @apply tw-h-3 tw-w-3 tw-shrink-0 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-400 tw-border-t-transparent;
    }
    .tile-reason {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .tile-actions {
      @apply tw-flex tw-flex-col tw-items-start;
    }
    .tile-link {
      @apply tw-min-h-11 tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-xs tw-font-medium tw-text-neutral-800 tw-underline tw-underline-offset-2;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .add-tile {
      @apply tw-flex tw-h-32 tw-w-24 tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-border tw-border-dashed tw-border-neutral-300 tw-bg-white tw-px-2 tw-text-center tw-text-xs tw-font-medium tw-text-neutral-600 tw-transition-colors hover:tw-border-neutral-400 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .file-input {
      @apply tw-hidden;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .v2-menu-capture-tray {
      .state-spinner {
        @apply tw-animate-none;
      }
      .progress-fill {
        @apply tw-transition-none;
      }
    }
  }
</style>
